import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, CalendarHeart, CalendarX } from "lucide-react";
import PageHero from "@/components/PageHero";
import SafetyNote from "@/components/SafetyNote";
import { Field, TextInput, TextArea, SelectInput } from "@/components/Field";
import { Reveal, PrimaryButton } from "@/components/primitives";
import Snippet from "@/components/Snippet";
import { api, formatApiError } from "@/lib/api";
import { SESSION_TYPES, LIB } from "@/data/content";
import { useSite } from "@/context/SiteContext";

function slotLabel(s) {
  let d = s.date;
  try {
    d = new Date(s.date + "T00:00:00").toLocaleDateString("en-GB", {
      weekday: "long", day: "numeric", month: "long", year: "numeric",
    });
  } catch { /* keep raw */ }
  return `${d} · ${s.time_slot}`;
}

export default function Book() {
  const { img, snip } = useSite();
  const sessionTypes = [
    snip("book.session_type_1", SESSION_TYPES[0]),
    snip("book.session_type_2", SESSION_TYPES[1]),
    snip("book.session_type_3", SESSION_TYPES[2]),
  ];
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    session_type: SESSION_TYPES[0],
    slot_id: "",
    notes: "",
    consent: false,
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/slots")
      .then(({ data }) => setSlots(data || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    if (!form.slot_id) {
      toast.error("Please choose an available session time.");
      return;
    }
    if (!form.consent) {
      toast.error("Please confirm you've read the wellbeing note to continue.");
      return;
    }
    const slot = slots.find((s) => s.id === form.slot_id);
    setSending(true);
    try {
      await api.post("/bookings", {
        ...form,
        preferred_date: slot?.date || "",
        time_slot: slot?.time_slot || "",
      });
      setDone(true);
      toast.success("Your request has been received with care.");
    } catch (err) {
      const status = err.response?.status;
      if (status === 409) {
        toast.error("That time has just been taken. Please choose another.");
        // refresh availability
        api.get("/slots").then(({ data }) => setSlots(data || [])).catch(() => {});
        setForm((f) => ({ ...f, slot_id: "" }));
      } else {
        toast.error(formatApiError(err.response?.data?.detail) || "Could not send your request.");
      }
    } finally {
      setSending(false);
    }
  };

  const noSlots = !loadingSlots && slots.length === 0;

  return (
    <div>
      <PageHero
        eyebrow={snip("book.hero_eyebrow", "Reserve your time")}
        script={snip("book.hero_script", "Book a")}
        title={snip("book.hero_title", "Session with Isabelle")}
        intro={snip("book.hero_intro", "Choose from the available session times below and Isabelle will personally confirm your booking by email. Every session is held in a safe, unhurried, women-only space.")}
        image={img("book_hero", LIB.lavenderField)}
        imageAlt="A peaceful lavender field in soft light"
      />

      <section className="py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="rounded-3xl border border-brand-line bg-white p-8 md:p-10">
              {done ? (
                <div className="text-center py-12" data-testid="book-success">
                  <CheckCircle2 className="mx-auto text-brand-forest mb-4" size={48} strokeWidth={1.25} />
                  <h3 className="font-heading text-3xl text-brand-ink mb-2"><Snippet k="book.success_heading" fallback="Request received" /></h3>
                  <p className="font-body text-brand-slate max-w-md mx-auto">
                    <Snippet k="book.success_text" fallback="Thank you. This is a request rather than a confirmed booking — Isabelle will be in touch by email to confirm your date and time, and to answer any questions. A confirmation email is on its way to you now." />
                  </p>
                </div>
              ) : noSlots ? (
                <div className="text-center py-12" data-testid="book-no-slots">
                  <CalendarX className="mx-auto text-brand-lavender-dark mb-4" size={48} strokeWidth={1.25} />
                  <h3 className="font-heading text-3xl text-brand-ink mb-2"><Snippet k="book.no_slots_heading" fallback="No open sessions right now" /></h3>
                  <p className="font-body text-brand-slate max-w-md mx-auto">
                    <Snippet k="book.no_slots_text" fallback="There are no available session times at the moment. Please leave a message and Isabelle will let you know as soon as new dates open up." />
                  </p>
                  <div className="mt-6">
                    <PrimaryButton as={Link} to="/contact" data-testid="book-contact-link">
                      <Snippet k="book.no_slots_cta" fallback="Send a message" />
                    </PrimaryButton>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-7" data-testid="book-form">
                  <div className="flex items-center gap-3 pb-2">
                    <CalendarHeart className="text-brand-forest" strokeWidth={1.5} />
                    <h2 className="font-heading text-2xl text-brand-ink"><Snippet k="book.form_heading" fallback="Your booking request" /></h2>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-7">
                    <Field label="Your name" required>
                      <TextInput data-testid="book-name" value={form.name} onChange={upd("name")} placeholder="Jane" />
                    </Field>
                    <Field label="Email" required>
                      <TextInput data-testid="book-email" type="email" value={form.email} onChange={upd("email")} placeholder="jane@email.com" />
                    </Field>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-7">
                    <Field label="Phone (optional)">
                      <TextInput data-testid="book-phone" value={form.phone} onChange={upd("phone")} placeholder="07700 900000" />
                    </Field>
                    <Field label="Session type" required>
                      <SelectInput data-testid="book-session-type" value={form.session_type} onChange={upd("session_type")}>
                        {sessionTypes.map((s) => <option key={s} value={s}>{s}</option>)}
                      </SelectInput>
                    </Field>
                  </div>

                  <Field label={<Snippet k="book.slot_label" fallback="Available session time" />} required hint={snip("book.slot_hint", "Only Isabelle's open session times are shown.")}>
                    <SelectInput data-testid="book-slot" value={form.slot_id} onChange={upd("slot_id")}>
                      <option value="">{loadingSlots ? "Loading available times…" : "Choose an available time…"}</option>
                      {slots.map((s) => (
                        <option key={s.id} value={s.id}>{slotLabel(s)}</option>
                      ))}
                    </SelectInput>
                  </Field>

                  <Field label="Anything you'd like Isabelle to know? (optional)">
                    <TextArea data-testid="book-notes" rows={4} value={form.notes} onChange={upd("notes")} placeholder="A little about what's drawing you to a session…" />
                  </Field>

                  <div className="rounded-2xl border border-brand-line bg-brand-accent/40 p-6" data-testid="booking-agreement">
                    <h3 className="font-heading text-xl text-brand-ink mb-3"><Snippet k="book.agreement_heading" fallback="Booking Agreement" /></h3>
                    <div className="space-y-3 font-body text-sm text-brand-slate leading-relaxed">
                      <p><Snippet k="book.agreement_p1" fallback="A QHHT session is the reservation of my professional time, preparation, and facilitation services. It is not the purchase of a guaranteed experience, past-life regression, subconscious communication, healing outcome, or specific result." /></p>
                      <p><Snippet k="book.agreement_p2" fallback="Each client's experience is unique and will unfold according to their own readiness and circumstances. No guarantees are made regarding what will be experienced, perceived, remembered, or achieved during the session." /></p>
                      <p><Snippet k="book.agreement_p3" fallback="By booking, you acknowledge that you are paying for the session itself and the time dedicated to you, regardless of the nature or depth of the experience that occurs." /></p>
                    </div>
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="book-consent"
                      checked={form.consent}
                      onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                      className="mt-1 h-4 w-4 accent-brand-forest"
                    />
                    <span className="font-body text-sm text-brand-slate leading-relaxed">
                      <Snippet k="book.consent" fallback="I have read the Booking Agreement above and understand that QHHT is a complementary wellbeing practice, not a medical or psychological treatment, and that I am 18 or over." />
                    </span>
                  </label>

                  <PrimaryButton type="submit" disabled={sending} data-testid="book-submit" className="disabled:opacity-60">
                    {sending ? "Sending…" : <Snippet k="book.submit" fallback="Request booking" />}
                  </PrimaryButton>
                </form>
              )}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <SafetyNote className="mt-12" />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
